import React from 'react';
import './PrivacyPage.css';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const PrivacyPage: React.FC = () => {
  useDocumentMeta({
    title: 'Privacy Policy — Heseda AI',
    description: 'Heseda AI privacy policy. Learn how we collect, use, and protect your skincare data.',
    path: '/privacy',
  });

  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <div className="privacy-logo">
          <img src="/logo-512-colorful.png" alt="Heseda AI Logo" />
        </div>
        
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: January 18, 2026</p>

        <p>Heseda AI ("we", "our", or "us") operates the Heseda mobile application and web service (the "Service"). Heseda AI is an AI-powered conversational skincare consultant that provides personalized product recommendations through natural dialogue. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.</p>

        <h2>1. Information We Collect</h2>
        
        <h3>1.1 Personal Information</h3>
        <p>We may collect the following personal information:</p>
        <ul>
          <li><strong>Account Information:</strong> Email address, name, and profile picture when you create an account or sign in via Google/Apple</li>
          <li><strong>Skin Profile Data:</strong> Skin type, concerns, preferences, and routines you provide during AI conversations</li>
          <li><strong>Photos:</strong> Images you optionally upload for skin analysis (processed by AI and not stored permanently)</li>
          <li><strong>Purchase Information:</strong> Order history, shipping address for samples, and payment details (processed securely via Stripe)</li>
          <li><strong>Sample Feedback:</strong> Your feedback on product samples to improve recommendations</li>
        </ul>

        <h3>1.2 Automatically Collected Information</h3>
        <ul>
          <li><strong>Device Information:</strong> Device type, operating system, and app version</li>
          <li><strong>Usage Data:</strong> Features used, interactions with the AI assistant, and session duration</li>
          <li><strong>Analytics:</strong> Anonymous usage statistics to improve our Service</li>
        </ul>

        <h2>2. How We Use Your Information</h2>
        <p>We use the collected information to:</p>
        <ul>
          <li>Provide personalized skincare recommendations through our AI assistant</li>
          <li>Build and maintain your skin profile for improved matching over time</li>
          <li>Process sample requests and product orders</li>
          <li>Send transactional emails (order confirmations, shipping updates, sample follow-ups)</li>
          <li>Improve and optimize our Service and recommendation algorithms</li>
          <li>Respond to your inquiries and provide customer support</li>
          <li>Send periodic emails about new features and products (with your consent)</li>
        </ul>

        <h2>3. Data Storage and Security</h2>
        <p>Your data is stored securely using:</p>
        <ul>
          <li><strong>Firebase/Google Cloud:</strong> For user accounts and profile data</li>
          <li><strong>Microsoft Azure:</strong> For application hosting and API services</li>
          <li><strong>Stripe:</strong> For secure payment processing (PCI-DSS compliant)</li>
        </ul>
        <p>All data transmission is encrypted using TLS/SSL. We implement industry-standard security measures to protect your information.</p>

        <h2>4. Data Sharing</h2>
        <p>We do NOT sell your personal information. We may share data with:</p>
        <ul>
          <li><strong>Service Providers:</strong> Third-party services that help us operate (Firebase, Azure, Stripe, OpenAI for AI processing)</li>
          <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
        </ul>

        <h2>5. Third-Party Services</h2>
        <p>Our Service uses the following third-party services:</p>
        <ul>
          <li><strong>OpenAI:</strong> For AI-powered skincare recommendations</li>
          <li><strong>Google Firebase:</strong> For authentication and data storage</li>
          <li><strong>Stripe:</strong> For payment processing</li>
          <li><strong>Google Search API:</strong> For product research</li>
        </ul>
        <p>Each service has its own privacy policy governing data use.</p>

        <h2>6. Your Rights</h2>
        <p>You have the right to:</p>
        <ul>
          <li><strong>Access:</strong> Request a copy of your personal data</li>
          <li><strong>Correction:</strong> Update or correct inaccurate information</li>
          <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
          <li><strong>Portability:</strong> Receive your data in a portable format</li>
          <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
        </ul>
        <p>To exercise these rights, contact us at the email below.</p>

        <h2>7. Data Retention</h2>
        <p>We retain your data for as long as your account is active or as needed to provide services. You may request account deletion at any time, and we will remove your personal data within 30 days.</p>

        <h2>8. Children's Privacy</h2>
        <p>Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.</p>

        <h2>9. International Users</h2>
        <p>If you are accessing our Service from outside the United States, please note that your data may be transferred to and processed in the United States and other countries where our service providers operate.</p>

        <h2>10. Changes to This Policy</h2>
        <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.</p>

        <h2>11. Contact Us</h2>
        <div className="contact-info">
          <p>If you have questions about this Privacy Policy or your data, please contact us:</p>
          <ul>
            <li><strong>Email:</strong> <a href="mailto:dev@heseda.com">dev@heseda.com</a></li>
            <li><strong>Website:</strong> <a href="https://heseda.ai">https://heseda.ai</a></li>
          </ul>
        </div>

        <div className="privacy-footer">
          <p>&copy; 2026 Heseda AI. All rights reserved.</p>
          <p><a href="/">Back to App</a></p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
